// Lib
import React, { ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { EditorContent } from '@tiptap/react';
import classNames from 'classnames';

// Utils
import { getCaptionModified, getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getCaptionEditorId, getCaptionEditorKey } from '../../../element/utils/elementEditorUtils';

// Hooks
import usePoiUpdateHandler from '../../pointsOfInterest/usePoiUpdateHandler';
import useTiptapInnerRef from '../../../../common/tiptap/hooks/useTiptapInnerRef';
import useClientElementTiptapEditor from '../../tiptapEditor/useClientElementTiptapEditor';
import { useSuggestionsManager } from '../../tiptapEditor/useSuggestionsManager';

// Extensions
import useCaptionTiptapEditorExtensions from './useCaptionTiptapEditorExtensions';

// Components
import { SearchHighlightObserver } from '../../../../common/tiptap/extensions/SearchHighlighter';

// Types
import { CaptionProps } from '../captionComponentTypes';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';

// Styles
import './CaptionTiptapEditor.scss';

type CaptionTiptapEditorProps = Omit<CaptionProps, 'textContent' | 'captionVisible'> & {
    textContent: TiptapContent;
};

const CaptionTiptapEditor = (props: CaptionTiptapEditorProps): ReactElement => {
    const {
        element,
        textContent,
        currentEditorId,
        isEditable,
        isEditing,
        isSingleSelected,
        spellCheck,
        placeholder,
        style,
        padTop,
        startEditing,
        saveCaptionContent,
        onEmptyBackspace,
        onDoubleClick,
    } = props;

    const dispatch = useDispatch();

    const editorId = getCaptionEditorId(props);
    const editorKey = getCaptionEditorKey(props);
    const elementId = getElementId(element);

    const { innerRef, contentInnerRef } = useTiptapInnerRef();
    const onHighlightsChanged = usePoiUpdateHandler({ contentInnerRef, elementId });

    const [suggestionsManager, mentionsPopup] = useSuggestionsManager();
    const extensions = useCaptionTiptapEditorExtensions({
        placeholder,
        onEmptyBackspace,
        saveCaptionContent,
        suggestionsManager,
        isEditable,
        dispatch,
        onHighlightsChanged,
    });

    const { editor, onMouseDown, onClick } = useClientElementTiptapEditor({
        persistedContent: textContent,
        extensions,

        editorId,
        editorKey,
        currentEditorId,

        isEditable: !!isEditable,
        isEditing: !!isEditing,
        isSingleSelected: !!isSingleSelected,

        startEditing,
        saveContent: saveCaptionContent,
    });

    const classes = classNames('Caption', {
        // This class is used in a querySelector to avoid changing the text automatically
        // when choosing a custom colour
        'caption-modified': getCaptionModified(element),
        'pad-top': padTop,
    });

    return (
        <div
            className={classes}
            style={style}
            onMouseDown={onMouseDown}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
        >
            {mentionsPopup}
            <SearchHighlightObserver editor={editor} />
            <EditorContent
                innerRef={innerRef}
                className="CaptionTiptapEditor"
                dir="auto"
                editor={editor}
                spellCheck={spellCheck}
            />
        </div>
    );
};

export default CaptionTiptapEditor;
